import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaTag } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import { Reveal } from "../../../components/common";
import samuraiHero from "../../../assets/course/samurai-hero.jpg";

const STATS = [
  { value: "8 Weeks", label: "Duration" },
  { value: "32", label: "Classes" },
  { value: "Ai + Ps", label: "Software" },
];

const CreativeSamuraiBanner = () => {
  return (
    <section className="pb-4 md:pb-6">
      <Reveal>
        <div>
          <Link
            to="/courses/creative-samurai"
            className="group relative block overflow-hidden rounded-2xl md:rounded-3xl focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary focus-visible:ring-offset-2"
            aria-label="Creative Samurai Volume 1: current batch running, next batch November 2026"
          >
            {/* Base */}
            <div className="absolute inset-0 bg-[#080808]" />

            {/* Hero image - right bleed */}
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={samuraiHero}
                alt=""
                aria-hidden="true"
                className="absolute right-0 top-0 h-full w-[58%] max-w-none object-cover object-center opacity-90 transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#080808] via-[#080808]/92 to-[#080808]/20" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-[#080808]/40 md:bg-gradient-to-t md:from-transparent md:via-transparent md:to-transparent" />
            </div>

            {/* Atmosphere */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_80%_at_15%_50%,rgba(232,0,13,0.18),transparent)]" />
            <div
              className="absolute inset-0 opacity-[0.035]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,.8) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.8) 1px,transparent 1px)",
                backgroundSize: "36px 36px",
              }}
            />

            {/* Diagonal slash accent */}
            <div
              className="absolute -right-8 top-0 bottom-0 w-24 bg-gradient-to-b from-transparent via-designer-primary/20 to-transparent skew-x-[-18deg] pointer-events-none"
              aria-hidden="true"
            />
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-designer-primary to-transparent opacity-80" />

            {/* Content */}
            <div className="relative z-10 p-6 md:p-10 lg:p-12">
              <div className="flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-10">

                <div className="flex-1 min-w-0 max-w-2xl">
                  {/* Badge row */}
                  <div className="flex flex-wrap items-center gap-2.5 mb-4">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold tracking-[0.18em] uppercase">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Batch Running
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-designer-primary/15 border border-designer-primary/30 text-designer-primary text-[10px] font-bold tracking-[0.18em] uppercase">
                      Next Batch · Nov 2026
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/8 border border-white/12 text-white/55 text-[10px] font-bold tracking-[0.18em] uppercase">
                      <HiSparkles className="w-3 h-3 text-designer-primary" />
                      Vol. 1
                    </span>
                  </div>

                  {/* Title */}
                  <p className="text-[10px] md:text-xs font-mono tracking-[0.22em] text-white/40 uppercase mb-2">
                    Arvix Creative · Professional Course
                  </p>
                  <h2 className="text-4xl sm:text-5xl md:text-6xl font-display font-extrabold text-white leading-[0.95] tracking-tight mb-3">
                    Creative
                    <span className="block bg-gradient-to-r from-designer-primary via-red-400 to-red-600 bg-clip-text text-transparent">
                      Samurai
                    </span>
                  </h2>
                  <p className="text-sm md:text-base text-white/50 font-medium tracking-wide mb-4">
                    Graphic Design · Beginner to Professional
                  </p>

                  <p className="text-white/55 text-sm md:text-base leading-relaxed mb-6 max-w-xl">
                    From zero to freelance-ready in 8 weeks. Master Illustrator &amp; Photoshop,
                    build a real portfolio, and start earning. 32 hands-on classes, real projects.
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-6 lg:mb-0">
                    {STATS.map((stat) => (
                      <div
                        key={stat.label}
                        className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 backdrop-blur-sm transition-colors duration-300 group-hover:border-designer-primary/25"
                      >
                        <div className="text-base md:text-lg font-display font-bold text-white leading-none">
                          {stat.value}
                        </div>
                        <div className="text-[9px] font-mono tracking-[0.22em] text-white/35 uppercase mt-1">
                          {stat.label}
                        </div>
                      </div>
                    ))}

                    {/* Course Fee - special discount card */}
                    <div className="px-3 py-2.5 rounded-lg bg-designer-primary/10 border border-designer-primary/30 backdrop-blur-sm transition-colors duration-300 group-hover:border-designer-primary/60">
                      <div className="flex items-baseline gap-1.5 flex-wrap leading-none mb-0.5">
                        <span className="text-base md:text-lg font-display font-bold text-designer-primary leading-none">৳4,400</span>
                        <span className="text-[10px] text-white/30 line-through leading-none">৳5,500</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-[9px] font-mono tracking-[0.22em] text-white/35 uppercase">Course Fee</span>
                        <span className="text-[9px] font-bold text-white bg-designer-primary px-1.5 py-0.5 rounded-full leading-none">20% OFF</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA block */}
                <div className="shrink-0 flex flex-col items-start lg:items-end gap-3">
                  <div className="flex flex-col items-start lg:items-end gap-1">
                    <div className="flex items-center gap-2 text-emerald-400/90 text-[10px] font-mono tracking-[0.22em] uppercase">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                      Current batch in progress
                    </div>
                    <p className="text-white/45 text-[10px] font-mono tracking-[0.22em] uppercase lg:text-right">
                      Next batch opens Nov 2026 · date TBA
                    </p>
                  </div>
                  <span className="group/btn inline-flex items-center justify-center gap-2.5 px-7 py-4 bg-designer-primary text-white font-bold text-sm rounded-xl shadow-lg shadow-red-500/35 transition-all duration-300 group-hover:bg-red-600 group-hover:shadow-red-500/50 group-hover:scale-[1.03] whitespace-nowrap">
                    View Course &amp; Waitlist
                    <FaArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </div>

            {/* Discount badge */}
            <div className="absolute top-4 right-4 flex items-center gap-1 px-2.5 py-1 rounded-full bg-designer-primary text-white text-[10px] font-bold tracking-wider shadow-lg">
              <FaTag className="w-2.5 h-2.5" />
              20% OFF
            </div>

            {/* Bottom accent */}
            <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-designer-primary via-red-500 to-transparent" />
          </Link>
        </div>
      </Reveal>
    </section>
  );
};

export default CreativeSamuraiBanner;
